<template>
    <div @keydown.esc="hideCalendar" :class="inputClass" v-click-outside="hideCalendar" class="zs-date-picker">

        <ZSInput readonly :label="label" @focus="showAndPositionCalendar()" :value="formattedDate || format"
            @click="showAndPositionCalendar()" ref="zsInput">
        </Zsinput>

        <div v-if="showCalendar" ref="zsCalendar" class="zs-calendar"
            :class="{ 'zs-calendar-top': isAbove, 'no-label': !label }">
            <div class="zs-calendar-header">
                <button class="zs-navigation-button" @click="previousMonth">
                    <ZSIcon color="white" icon="ChevronLeft" />
                </button>
                <div @click="toggleYearMonthSelector" class="zs-year-month"><span>{{ currentMonthName }} {{ currentYear
                }}</span></div>
                <button class="zs-navigation-button" @click="nextMonth">
                    <ZSIcon color="white" icon="ChevronRight" />
                </button>
            </div>
            <div v-if="showYearMonthSelector" class="zs-year-month-selector">
                <ZSSelect size="small" :placeholder="yearLabel" class="zs-year-select" v-model="selectedYear"
                    :items="formattedYears" itemLabel="label" itemValue="value"
                    @update:modelValue="updateCurrentDate(false)" />
                <ZSSelect size="small" :placeholder="monthLabel" class=" zs-month-select" v-model="selectedMonth"
                    :items="formattedMonths" itemLabel="label" itemValue="value"
                    @update:modelValue="updateCurrentDate" />
            </div>
            <div class="zs-calendar-body">
                <div v-for="day in localizedDaysOfWeek" :key="day" class="zs-day-header">
                    {{ day }}
                </div>
                <div v-for="date in calendarDates" :key="date.getTime()" class="zs-calendar-date"
                    :class="{ 'current-month': isCurrentMonth(date), 'selected': isSelected(date), 'today': isToday(date) }"
                    @click="selectDate(date)">
                    {{ date.getDate() }}
                </div>
            </div>
            <div class="zs-calendar-footer">
                <button @click="clearSelectedDate" class="footer-btn">{{ clearButtonText }}</button>
                <button @click="setCurrentDateToToday" class="footer-btn">{{ todayButtonText }}</button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import ZSIcon from '../global/ZSIcon.vue';
import ZSSelect from './ZSSelect.vue';
import ZSInput from './ZSInput.vue';


const props = defineProps({
    label: {
        type: String,
        default: ''
    },
    modelValue: { // Change from value to modelValue
        type: [String, Date],
        default: null
    },
    format: {
        type: String,
        default: 'YYYY-MM-DD'
    },
    locale: {
        type: String,
        default: 'pt',
        validator: (value) => ['pt', 'en', 'es'].includes(value)
    },
    inputClass: {
        type: String,
        default: ''
    }

});

const emit = defineEmits(['update:modelValue', 'formated-date']); // Change from 'input' to 'update:modelValue'

// Refs
const zsInput = ref(null);
const zsCalendar = ref(null);
const showCalendar = ref(false);
const showYearMonthSelector = ref(false);
const isAbove = ref(false);

// Initialize from props.modelValue instead of props.value
const selectedDate = ref(null);
const currentDate = ref(new Date());
const selectedYear = ref(new Date().getFullYear());
const selectedMonth = ref(new Date().getMonth());

// Initialize selectedDate and currentDate properly
onMounted(() => {
    if (props.modelValue) {
        selectedDate.value = typeof props.modelValue === 'string'
            ? parseDate(props.modelValue, props.format)
            : props.modelValue;

        currentDate.value = selectedDate.value || new Date();
        selectedYear.value = currentDate.value.getFullYear();
        selectedMonth.value = currentDate.value.getMonth();
    }
});

// Localization objects
const localization = {
    monthLabels: {
        pt: "Mês",
        en: "Month",
        es: "Mes"
    },
    yearLabels: {
        pt: "Ano",
        en: "Year",
        es: "Año"
    },
    dayLabels: {
        pt: "Dia",
        en: "Day",
        es: "Día"
    },
    daysOfWeek: {
        pt: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
        en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        es: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    },
    monthNames: {
        pt: [
            'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
            'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
        ],
        en: [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ],
        es: [
            'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
        ]
    },
    buttons: {
        clear: {
            pt: 'Limpar',
            en: 'Clear',
            es: 'Borrar'
        },
        today: {
            pt: 'Hoje',
            en: 'Today',
            es: 'Hoy'
        }
    }
};

// Computed localized properties
const localizedDaysOfWeek = computed(() => {
    return localization.daysOfWeek[props.locale] || localization.daysOfWeek.pt;
});

const monthNames = computed(() => {
    return localization.monthNames[props.locale] || localization.monthNames.pt;
});

const clearButtonText = computed(() => {
    return localization.buttons.clear[props.locale] || localization.buttons.clear.pt;
});

const todayButtonText = computed(() => {
    return localization.buttons.today[props.locale] || localization.buttons.today.pt;
});

// Computed properties
const formattedDate = computed(() => {
    if (typeof selectedDate.value === 'string') {
        return selectedDate.value;
    }
    return selectedDate.value
        ? formatDate(selectedDate.value, props.format)
        : '';
});

const currentMonthName = computed(() => {
    return monthNames.value[currentDate.value.getMonth()];
});

const currentYear = computed(() => {
    return currentDate.value.getFullYear();
});

const calendarDates = computed(() => {
    const year = currentDate.value.getFullYear();
    const month = currentDate.value.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const dates = [];
    for (let i = 0; i < firstDay.getDay(); i++) {
        dates.push(new Date(year, month, -i));
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
        dates.push(new Date(year, month, i));
    }

    const remainingDays = 7 - (dates.length % 7);
    if (remainingDays < 7) {
        for (let i = 1; i <= remainingDays; i++) {
            dates.push(new Date(year, month + 1, i));
        }
    }

    return dates;
});

const yearRange = computed(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 201 }, (_, i) => currentYear - 100 + i);
});

const formattedYears = computed(() => {
    return yearRange.value.map(year => ({ label: year, value: year }));
});

const formattedMonths = computed(() => {
    return monthNames.value.map((month, index) => ({ label: month, value: index }));
});

const monthLabel = computed(() => {
    return localization.monthLabels[props.locale] || localization.monthLabels.pt;
});

const yearLabel = computed(() => {
    return localization.yearLabels[props.locale] || localization.yearLabels.pt;
});

const dayLabel = computed(() => {
    return localization.dayLabels[props.locale] || localization.dayLabels.pt;
});
// Methods
function formatDate(date, format) {
    if (!date) return '';
    const map = {
        YYYY: date.getFullYear(),
        MM: String(date.getMonth() + 1).padStart(2, '0'),
        DD: String(date.getDate()).padStart(2, '0'),
        M: date.getMonth() + 1,
        D: date.getDate(),
    };
    return format.replace(/YYYY|MM|DD|M|D/g, matched => map[matched]);
}

function parseDate(dateString, format) {
    if (!dateString) return new Date();
    const parts = {};
    const formatParts = format.match(/YYYY|MM|DD|M|D/g);
    const dateParts = dateString.split(/[-/]/);

    if (formatParts.length !== dateParts.length) {
        throw new Error('Invalid date string or format');
    }

    formatParts.forEach((part, index) => {
        parts[part] = parseInt(dateParts[index], 10);
    });

    if (parts.YYYY && (parts.MM || parts.M) && (parts.DD || parts.D)) {
        const month = parts.MM ? parts.MM - 1 : parts.M - 1;
        const day = parts.DD || parts.D;
        return new Date(parts.YYYY, month, day);
    } else {
        throw new Error('Invalid date string or format');
    }
}

function hideCalendar() {
    if (showCalendar.value) {
        showCalendar.value = false;
    }
}

function isToday(date) {
    const today = new Date();
    return date.toDateString() === today.toDateString();
}

function isCurrentMonth(date) {
    return date.getMonth() === currentDate.value.getMonth();
}

function isSelected(date) {
    return selectedDate.value && date.toDateString() === selectedDate.value.toDateString();
}

function selectDate(date) {
    selectedDate.value = date;
    showCalendar.value = false;

    if (date) {
        // Use update:modelValue instead of input
        emit("formated-date", formatDate(date, props.format));
        emit('update:modelValue', typeof props.modelValue === 'string' ? formatDate(date, props.format) : date);
    }
}

function previousMonth() {
    currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() - 1, 1);
    updateSelectedYearMonth();
}

function nextMonth() {
    currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 1);
    updateSelectedYearMonth();
}

function toggleYearMonthSelector() {
    showYearMonthSelector.value = !showYearMonthSelector.value;
}

function updateCurrentDate(to_hide = true) {
    currentDate.value = new Date(selectedYear.value, selectedMonth.value, 1);
    showYearMonthSelector.value = to_hide ? false : true;
}

function updateSelectedYearMonth() {
    selectedYear.value = currentDate.value.getFullYear();
    selectedMonth.value = currentDate.value.getMonth();
}

function setCurrentDateToToday() {
    currentDate.value = new Date();
    updateSelectedYearMonth();
    selectDate(currentDate.value);
}

function clearSelectedDate() {
    selectedDate.value = null;
    emit("formated-date", '');
    emit('update:modelValue', typeof props.modelValue === 'string' ? '' : null);
}

function positionCalendar() {
    if (!showCalendar.value) return;
    const inputRect = zsInput.value.getRef().getBoundingClientRect();
    const calendarRect = zsCalendar.value.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Check if there's enough space below the input
    if (inputRect.bottom + calendarRect.height + 20 <= viewportHeight) {
        isAbove.value = false;
    } else if (inputRect.top - calendarRect.height - 20 >= 0) {
        // Check if there's enough space above the input
        isAbove.value = true;
    } else {
        // If there's not enough space above or below, position it where there's more space
        isAbove.value = (inputRect.top > viewportHeight - inputRect.bottom);
    }

    // Adjust horizontal position if calendar overflows horizontally
    const viewportWidth = window.innerWidth;
    if (inputRect.left + calendarRect.width > viewportWidth) {
        zsCalendar.value.style.right = '0';
        zsCalendar.value.style.left = 'auto';
    }
}

function onBlur(event) {
    // Delay hiding the calendar to allow for calendar interactions
    setTimeout(() => {
        if (!document.activeElement.closest('.zs-date-picker')) {
            showCalendar.value = false;
        }
    }, 100);
}

function showAndPositionCalendar() {
    showCalendar.value = true;
    nextTick(() => {
        positionCalendar();
    });
}

// Watch for changes to modelValue prop instead of value
watch(() => props.modelValue, (newValue) => {
    if (newValue !== undefined && newValue !== null) {
        selectedDate.value = typeof newValue === 'string' ? parseDate(newValue, props.format) : newValue;

        // Update currentDate only when modelValue changes from external sources
        if (!selectedDate.value?.isSameDay || !currentDate.value?.isSameDay ||
            !selectedDate.value.isSameDay(currentDate.value)) {
            currentDate.value = new Date(selectedDate.value);
            updateSelectedYearMonth();
        }
    } else {
        selectedDate.value = null;
    }
}, { immediate: true });

// Lifecycle hooks
onMounted(() => {
    window.addEventListener('resize', positionCalendar);
    window.addEventListener('scroll', positionCalendar);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', positionCalendar);
    window.removeEventListener('scroll', positionCalendar);
});
</script>

<style scoped>
.zs-date-picker {
    --zs-datepicker-font-family: inherit;
    --zs-datepicker-calendar-z-index: 10;
    --zs-datepicker-calendar-bg-color: white;
    --zs-datepicker-calendar-border: 1px solid #ddd;
    --zs-datepicker-calendar-padding: 10px;
    --zs-datepicker-calendar-border-radius: 4px;
    --zs-datepicker-calendar-min-height: 250px;
    --zs-datepicker-calendar-transition: opacity .2s ease, transform .2s ease;
    --zs-datepicker-header-margin-bottom: 10px;
    --zs-datepicker-body-gap: 1px;
    --zs-datepicker-day-header-font-family: inherit;
    --zs-datepicker-day-header-font-weight: bold;
    --zs-datepicker-date-padding: 5px;
    --zs-datepicker-date-border-radius: 4px;
    --zs-datepicker-date-border: 1px solid transparent;
    --zs-datepicker-date-color: #333;
    --zs-datepicker-date-max-height: 30px;
    --color-button-primary: var(--color-orange);
    --color-button-primary-hover: #357ABD;
    --color-text-primary: white;
    --zs-datepicker-current-month-font-family: inherit;
    --zs-datepicker-current-month-font-weight: normal;
    --zs-datepicker-current-month-color: #333;
    --zs-datepicker-today-border: 1px solid var(--color-button-primary);
    --zs-datepicker-today-bg-color: #D4E4F7;
    --zs-datepicker-today-color: var(--color-button-primary);
    --zs-datepicker-selected-bg-color: var(--color-button-primary);
    --zs-datepicker-selected-color: var(--color-text-primary);
    --zs-datepicker-selector-margin-bottom: 10px;
    --zs-datepicker-selector-gap: 5px;
    --zs-datepicker-nav-button-border-radius: 4px;
    --zs-datepicker-nav-button-width: 28px;
    --zs-datepicker-nav-button-height: 28px;
    --zs-datepicker-year-month-gap: 5px;
    --zs-datepicker-year-month-border-bottom: 1px dashed #aaa;
    --zs-datepicker-year-month-hover-border: 1px solid var(--color-button-primary);
    --zs-datepicker-footer-margin-top: 10px;
    --zs-datepicker-footer-btn-border-radius: 4px;
    --zs-datepicker-footer-btn-padding: 6px 12px;
    --zs-datepicker-footer-btn-font-size: 12px;

    position: relative;
    display: inline-block;
    font-family: var(--zs-datepicker-font-family);
    cursor: pointer;
}

.zs-calendar {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: var(--zs-datepicker-calendar-z-index);
    background-color: var(--zs-datepicker-calendar-bg-color);
    border: var(--zs-datepicker-calendar-border);
    padding: var(--zs-datepicker-calendar-padding);
    border-radius: var(--zs-datepicker-calendar-border-radius);
    min-height: var(--zs-datepicker-calendar-min-height);
    display: flex;
    flex-direction: column;
    transition: var(--zs-datepicker-calendar-transition);
}

.zs-calendar-top {
    top: auto;
    bottom: calc(100% - 23px);
    transition: var(--zs-datepicker-calendar-transition);

    &.no-label {
        bottom: 100%;
    }
}

.zs-calendar-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--zs-datepicker-header-margin-bottom);
    align-items: center;
}

.zs-calendar-body {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: var(--zs-datepicker-body-gap);
    flex: 1;
}

.zs-day-header {
    font-family: var(--zs-datepicker-day-header-font-family);
    font-weight: var(--zs-datepicker-day-header-font-weight);
    text-align: center;
}

.zs-calendar-date {
    text-align: center;
    cursor: pointer;
    padding: var(--zs-datepicker-date-padding);
    border-radius: var(--zs-datepicker-date-border-radius);
    border: var(--zs-datepicker-date-border);
    color: var(--zs-datepicker-date-color);
    max-height: var(--zs-datepicker-date-max-height);
}

.zs-calendar-date:hover {
    background-color: var(--color-button-primary-hover);
    color: var(--color-text-primary);
}

.current-month {
    font-family: var(--zs-datepicker-current-month-font-family);
    font-weight: var(--zs-datepicker-current-month-font-weight);
    text-transform: uppercase;
    color: var(--zs-datepicker-current-month-color);
}

.today {
    border: var(--zs-datepicker-today-border);
    background-color: var(--zs-datepicker-today-bg-color);
    color: var(--zs-datepicker-today-color);
}

.selected {
    background-color: var(--zs-datepicker-selected-bg-color);
    color: var(--zs-datepicker-selected-color);
}

.zs-year-month-selector {
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--zs-datepicker-selector-margin-bottom);
    gap: var(--zs-datepicker-selector-gap);
}

.zs-navigation-button {
    background-color: var(--color-button-primary);
    color: var(--color-text-primary);
    border: none;
    border-radius: var(--zs-datepicker-nav-button-border-radius);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;
    width: var(--zs-datepicker-nav-button-width);
    height: var(--zs-datepicker-nav-button-height);
}

.zs-navigation-button:hover {
    background-color: var(--color-button-primary-hover);
}

.zs-year-month {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--zs-datepicker-year-month-gap);
    border-bottom: var(--zs-datepicker-year-month-border-bottom);

    &:hover {
        cursor: pointer;
        border-bottom: var(--zs-datepicker-year-month-hover-border);
    }
}

.zs-calendar-footer {
    display: flex;
    justify-content: space-between;
    margin-top: var(--zs-datepicker-footer-margin-top);

    .footer-btn {
        background-color: var(--color-button-primary);
        color: var(--color-text-primary);
        border: none;
        border-radius: var(--zs-datepicker-footer-btn-border-radius);
        padding: var(--zs-datepicker-footer-btn-padding);
        cursor: pointer;
        font-size: var(--zs-datepicker-footer-btn-font-size);
    }

    .footer-btn:hover {
        background-color: var(--color-button-primary-hover);
    }
}

select.zs-year-select,
select.zs-month-select {
    border: none;

    option {
        border: none;
        background-color: white;
    }
}
</style>